import React from 'react';
import { Copy, CheckCircle, FileText, Clock, Hash, MessageSquare, Share2 } from 'lucide-react';
import { APP_CONFIG, REPORT_CONFIG } from '../config';
import { getReportSections } from '../utils/generatorConfig';
import { t } from '../utils/i18n';

const ResultDisplay = ({ result, prompt, onCopy, copied }) => {
  if (!result) return null;

  // 格式化结果内容
  const formatContent = (content) => {
    if (!content) return '';
    return content
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  };

  const displayContent = result.answer || result.content || result.text || result.response || t('noResponse');
  const reportSections = getReportSections(result, REPORT_CONFIG.result.sections);
  const copyContent = reportSections.length > 0
    ? reportSections.map((section) => `${section.label}\n${section.content}`).join('\n\n')
    : displayContent;

  const handleShare = async () => {
    const shareText = `${REPORT_CONFIG.share.copyText}\n\n${copyContent}`;
    if (navigator.share) {
      await navigator.share({
        title: REPORT_CONFIG.share.title,
        text: shareText,
      });
      return;
    }
    onCopy(shareText);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
        {/* 头部 */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{REPORT_CONFIG.result.title || APP_CONFIG.REPORT_LABEL}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all duration-200 font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                <Share2 className="w-4 h-4" />
                <span>{t('share')}</span>
              </button>
              <button
                onClick={() => onCopy(copyContent)}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-all duration-200 font-medium ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-blue-100'
                }`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>{t('copied')}</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>{t('copy')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* 提示词显示 */}
        {prompt && (
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mt-0.5">
                <MessageSquare className="w-3 h-3 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 mb-1">{t('input')}</p>
                <p className="text-gray-700 text-sm leading-relaxed bg-white/50 p-3 rounded-lg border border-blue-100">
                  "{prompt}"
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 结果内容 */}
        <div className="px-6 py-8 space-y-6">
          {reportSections.length > 0 ? (
            reportSections.map((section) => (
              <section key={section.key} className="rounded-xl border border-gray-100 bg-gray-50/70 p-5">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{section.label}</h4>
                <div
                  className="prose prose-gray max-w-none text-gray-800 leading-relaxed prose-headings:text-gray-900 prose-strong:text-gray-900 prose-em:text-gray-700"
                  dangerouslySetInnerHTML={{ __html: formatContent(String(section.content)) }}
                />
              </section>
            ))
          ) : (
            <div
              className="prose prose-gray max-w-none text-gray-800 leading-relaxed prose-headings:text-gray-900 prose-headings:border-b prose-headings:border-gray-100 prose-headings:pb-2 prose-strong:text-gray-900 prose-em:text-gray-700"
              dangerouslySetInnerHTML={{ __html: formatContent(displayContent) }}
            />
          )}
        </div>

        {/* 元数据 */}
        {(result.metadata?.usage?.input_tokens || result.metadata?.usage?.output_tokens || result.input_tokens || result.output_tokens || result.response_time) && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex flex-wrap gap-6 text-sm">
              {(result.metadata?.usage?.input_tokens || result.input_tokens) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Hash className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">{t('input')}</span>
                  <span className="text-gray-800 font-mono">{result.metadata?.usage?.input_tokens || result.input_tokens}</span>
                  <span className="text-gray-500">{t('tokens')}</span>
                </div>
              )}
              {(result.metadata?.usage?.output_tokens || result.output_tokens) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Hash className="w-4 h-4 text-green-500" />
                  <span className="font-medium">{t('output')}</span>
                  <span className="text-gray-800 font-mono">{result.metadata?.usage?.output_tokens || result.output_tokens}</span>
                  <span className="text-gray-500">{t('tokens')}</span>
                </div>
              )}
              {result.response_time && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4 text-purple-500" />
                  <span className="font-medium">{t('time')}</span>
                  <span className="text-gray-800 font-mono">{result.response_time}ms</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultDisplay;
